import { useState, useEffect } from "react";
import { Trash2, Phone, Mail, MessageSquare as MsgIcon, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listLeads, deleteLead, listConversations, getConversation } from "@/lib/api";

const methodIcon = { phone: Phone, email: Mail, whatsapp: MsgIcon };

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null); // {sid, msgs}

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([listLeads(), listConversations()]);
      setLeads(l);
      setConvos(c);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openConvo = async (sid) => {
    const msgs = await getConversation(sid);
    setView({ sid, msgs });
  };

  const del = async (id) => { await deleteLead(id); toast.success("Silindi"); load(); };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="font-display">Gelen İletişim Talepleri ({leads.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
            leads.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">Henüz iletişim talebi yok</div>
            ) : (
              <div className="space-y-2">
                {leads.map((l) => {
                  const Icon = methodIcon[l.contact_method] || Phone;
                  return (
                    <div key={l.id} data-testid={`lead-row-${l.id}`} className="p-4 border border-border/40 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{l.name}</div>
                            <Badge variant="secondary" className="text-xs"><Icon className="w-3 h-3 mr-1" /> {l.contact_value}</Badge>
                            <Badge variant="outline" className="text-xs">{l.language?.toUpperCase()}</Badge>
                            <Badge variant="outline" className="text-xs">{l.country}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">{new Date(l.ts).toLocaleString()}</div>
                          {l.summary && (
                            <pre className="mt-2 text-xs bg-secondary/50 p-3 rounded-lg whitespace-pre-wrap font-body max-h-40 overflow-y-auto">{l.summary}</pre>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openConvo(l.session_id)} data-testid={`lead-view-${l.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => del(l.id)} className="text-destructive" data-testid={`lead-del-${l.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">Tüm Konuşmalar ({convos.length})</CardTitle></CardHeader>
        <CardContent>
          {convos.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8">Henüz konuşma yok</div>
          ) : (
            <div className="space-y-2">
              {convos.map((c) => (
                <div key={c.session_id} data-testid={`convo-${c.session_id}`} className="flex items-center justify-between p-3 border border-border/40 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline">{(c.language || "tr").toUpperCase()}</Badge>
                      <Badge variant="outline">{c.country}</Badge>
                      <span className="text-muted-foreground">{new Date(c.last_ts).toLocaleString()}</span>
                      <span className="text-muted-foreground">· {c.count} mesaj</span>
                    </div>
                    <div className="text-sm truncate mt-1">{c.last_message}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openConvo(c.session_id)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Konuşma Detayı</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {view?.msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${m.role === "user" ? "text-white" : "bg-secondary"}`}
                  style={m.role === "user" ? { background: "#B39B7A" } : {}}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
